import {
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { TransactionsFileStateStepper as SharedTransactionsFileStateStepper } from '@monorepo/shared-lib';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import React from 'react';
import { useTranslation } from 'react-i18next';

export type { TransactionsFileStateTab } from '@monorepo/shared-lib';

interface Props {
  activeTab: 'CAPTURED' | 'REVIEWED' | 'POSTED';
  showPosted: boolean;
  counts: { CAPTURED: number; REVIEWED: number };
  onTabChange: (tab: 'CAPTURED' | 'REVIEWED' | 'POSTED') => void;
  /** Defaults to Ready to Post; Receipts/Excel use Reviewed via parent. */
  reviewedTabLabelKey?: string;
  /**
   * PWA file panel: render Capture-style circular actions (not pill tabs).
   * Uses the same orb classes as the Capture hero bottom icons.
   */
  variant?: 'pills' | 'orbs';
}

type OrbStep = {
  id: 'CAPTURED' | 'REVIEWED';
  label: string;
  tone: 'pending-review' | 'ready-to-post';
  count: number;
  icon: React.ReactNode;
};

/**
 * Capture-circle file-state switcher for PWA. Intentionally does not use
 * `transactions-file-state-tab` pills so shared stepper chrome cannot win.
 */
const TransactionsFileStateOrbs: React.FC<{
  activeTab: 'CAPTURED' | 'REVIEWED' | 'POSTED';
  counts: { CAPTURED: number; REVIEWED: number };
  onTabChange: (tab: 'CAPTURED' | 'REVIEWED' | 'POSTED') => void;
  pendingLabel: string;
  reviewedLabel: string;
  ariaLabel: string;
  formatCount: (n: number) => string;
}> = ({
  activeTab,
  counts,
  onTabChange,
  pendingLabel,
  reviewedLabel,
  ariaLabel,
  formatCount,
}) => {
  const steps: OrbStep[] = [
    {
      id: 'CAPTURED',
      label: pendingLabel,
      tone: 'pending-review',
      count: counts.CAPTURED,
      icon: <ClockCircleOutlined aria-hidden />,
    },
    {
      id: 'REVIEWED',
      label: reviewedLabel,
      tone: 'ready-to-post',
      count: counts.REVIEWED,
      icon: <CheckCircleOutlined aria-hidden />,
    },
  ];

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="scope-pwa-file-state-orbs"
    >
      {steps.map((step) => {
        const selected = activeTab === step.id;
        return (
          <button
            key={step.id}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={`${step.label} (${formatCount(step.count)})`}
            {...(step.id === 'REVIEWED'
              ? { 'data-tour-ready-to-post-tab': true }
              : {})}
            className={[
              'scope-pwa-capture-hero__action',
              'scope-pwa-file-state-orbs__action',
              `scope-pwa-file-state-orbs__action--${step.tone}`,
              selected ? 'scope-pwa-file-state-orbs__action--selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onTabChange(step.id)}
          >
            <span
              className="scope-pwa-capture-hero__action-orb scope-pwa-file-state-orbs__orb"
              aria-hidden
            >
              {step.icon}
            </span>
            <span className="scope-pwa-file-state-orbs__label">
              {step.label} ({formatCount(step.count)})
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const TransactionsFileStateStepper: React.FC<Props> = ({
  activeTab,
  showPosted,
  counts,
  onTabChange,
  reviewedTabLabelKey = 'organisation.transactions-view-tab-reviewed',
  variant = 'pills',
}) => {
  const { t, i18n } = useTranslation();
  const formatCount = (n: number) => formatDisplayInteger(n, i18n.language);

  if (variant === 'orbs') {
    const reviewedLabel =
      reviewedTabLabelKey === 'organisation.file-badge-reviewed'
        ? t('organisation.file-badge-reviewed')
        : t('organisation.transactions-file-state-to-post');

    return (
      <TransactionsFileStateOrbs
        activeTab={activeTab}
        counts={counts}
        onTabChange={onTabChange}
        ariaLabel={t('organisation.transactions-view-tablist-aria')}
        pendingLabel={t('organisation.transactions-file-state-to-review')}
        reviewedLabel={reviewedLabel}
        formatCount={formatCount}
      />
    );
  }

  return (
    <SharedTransactionsFileStateStepper
      activeTab={activeTab}
      showPosted={showPosted}
      counts={counts}
      onTabChange={onTabChange}
      ariaLabel={t('organisation.transactions-view-tablist-aria')}
      pendingLabel={t('organisation.transactions-view-tab-pending-review')}
      reviewedLabel={t(reviewedTabLabelKey)}
      postedLabel={t('organisation.transactions-view-tab-posted')}
      formatCount={formatCount}
    />
  );
};
